import React from "react";
import { cn } from "../../lib/utils";

interface TextParserProps {
  text: string;
  className?: string;
  emailClassName?: string;
  urlClassName?: string;
  hashtagClassName?: string;
  phoneClassName?: string;
}

const DEFAULT_ACTION_CLASSNAME =
  "text-secondary hover:text-accent underline cursor-pointer transition-all duration-300";

export default function TextParser({
  text,
  className,
  emailClassName = DEFAULT_ACTION_CLASSNAME,
  urlClassName = DEFAULT_ACTION_CLASSNAME,
  hashtagClassName = DEFAULT_ACTION_CLASSNAME,
  phoneClassName = DEFAULT_ACTION_CLASSNAME,
}: TextParserProps) {
  // Regex patterns for detection
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const hashtagRegex = /#[\w\u0590-\u05ff]+/g;
  // Phone regex matches various formats: +1 (555) 123-4567, (555) 123-4567, 555-123-4567, 555.123.4567, +33 1 23 45 67 89, etc.
  const phoneRegex =
    /(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})|(?:\+[1-9]\d{0,3}[-.\s]?)?(?:\(?[0-9]{1,4}\)?[-.\s]?){1,4}[0-9]{1,4}/g;

  // Function to parse text and create clickable elements
  const parseText = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Combine all matches with their positions
    const matches: Array<{
      type: "email" | "url" | "hashtag" | "phone";
      match: string;
      index: number;
    }> = [];

    // Find all emails
    let emailMatch;
    while ((emailMatch = emailRegex.exec(text)) !== null) {
      matches.push({
        type: "email",
        match: emailMatch[0],
        index: emailMatch.index,
      });
    }

    // Find all URLs
    let urlMatch;
    while ((urlMatch = urlRegex.exec(text)) !== null) {
      matches.push({
        type: "url",
        match: urlMatch[0],
        index: urlMatch.index,
      });
    }

    // Find all hashtags
    let hashtagMatch;
    while ((hashtagMatch = hashtagRegex.exec(text)) !== null) {
      matches.push({
        type: "hashtag",
        match: hashtagMatch[0],
        index: hashtagMatch.index,
      });
    }

    // Find all phone numbers
    let phoneMatch;
    while ((phoneMatch = phoneRegex.exec(text)) !== null) {
      matches.push({
        type: "phone",
        match: phoneMatch[0],
        index: phoneMatch.index,
      });
    }

    // Sort matches by position
    matches.sort((a, b) => a.index - b.index);

    // Process matches and create elements
    matches.forEach((match, i) => {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Create clickable element based on type
      const handleClick = () => {
        switch (match.type) {
          case "email":
            window.open(`mailto:${match.match}`, "_blank");
            break;
          case "url":
            window.open(match.match, "_blank", "noopener,noreferrer");
            break;
          case "hashtag":
            // You can customize this behavior - maybe navigate to a search page
            console.log(`Hashtag clicked: ${match.match}`);
            break;
          case "phone":
            window.open(`tel:${match.match}`, "_blank");
            break;
        }
      };

      const elementClassName =
        match.type === "email"
          ? emailClassName
          : match.type === "url"
          ? urlClassName
          : match.type === "hashtag"
          ? hashtagClassName
          : phoneClassName;

      parts.push(
        <span
          key={`${match.type}-${i}`}
          className={elementClassName}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          {match.match}
        </span>
      );

      lastIndex = match.index + match.match.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  return (
    <span className={cn("break-words whitespace-pre-wrap", className)}>
      {parseText(text)}
    </span>
  );
}
